import React, { useEffect, useState } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import Chart from 'react-apexcharts';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ReportFileExports from 'components/Reports/FileExports';
import ApiHelper from 'utils/apiHelper';
import { gridSpacing, dateRangeOptions } from 'utils/constants';
import { handleUpdateReportDateRange, convertDate } from 'utils/commonHelpers';
import DeviceTimeDevicesDialog from './DeviceTimeDevicesDialog';

const initialState = {
    values: [],
    dates: []
};

const ReportsDeviceTime = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const currentDate = new Date();
    const today = new Date();
    const [startDate, setStartDate] = useState(currentDate.setMonth(currentDate.getMonth() - 1));
    const [endDate, setEndDate] = useState(today.setDate(today.getDate() - 1));
    const [dateRangeValue, setDateRangeValue] = useState('1month');
    const [groupId, setGroupId] = useState(0);
    const [groupsData, setGroupsData] = useState([]);
    const [deviceVsTimeData, setDeviceVsTimeData] = useState(initialState);
    const [showDevices, setShowDevices] = useState(false);
    const [selectedDate, setSelectedDate] = useState('');
    const [filtersData, setFiltersData] = useState([]);

    useEffect(() => {
        getGroups();
    }, []);

    const getGroups = () => {
        ApiHelper.get('/api/reports/groups/')
            .then((res) => {
                setGroupsData(res.data);
            })
            .catch();
    };

    const handleChangeDateRangeValue = (e) => {
        const value = e.target.value;
        setDateRangeValue(value);
        const updatedStartDate = handleUpdateReportDateRange(value);
        setStartDate(updatedStartDate);
    };

    const getDeviceTimeData = () => {
        if (convertDate(startDate) === 'Invalid date') {
            toast.error('Fail! The start date format is incorrect');
            return true;
        }
        if (convertDate(endDate) === 'Invalid date') {
            toast.error('Fail! The end date format is incorrect');
            return true;
        }
        if (moment(convertDate(startDate)) > moment(convertDate(endDate))) {
            toast.error('Fail! The start date can not be greater than the end date');
            return true;
        }
        setLoading(true);
        ApiHelper.post('/api/reports/device_vs_time/', {
            start_date: convertDate(startDate),
            end_date: convertDate(endDate),
            group_id: groupId === 0 ? '' : groupId.toString()
        })
            .then((res) => {
                setDeviceVsTimeData(res.data);
                setLoading(false);
                let groupName = 'Any';
                for (let i = 0; i < groupsData.length; i++) {
                    if (groupsData[i].id === groupId) {
                        groupName = groupsData[i].name;
                    }
                }
                const temp = [
                    { label: 'Start Date', value: convertDate(startDate) },
                    { label: 'End Date', value: convertDate(endDate) },
                    { label: 'Group Name', value: groupName }
                ];
                setFiltersData(temp);
            })
            .catch(() => {
                setDeviceVsTimeData(initialState);
                setLoading(false);
                toast.warning('There is no device vs time data');
            });
    };

    const series = [
        {
            name: 'Device vs Time',
            data: deviceVsTimeData.values
        }
    ];
    const options = {
        chart: {
            zoom: {
                enabled: false
            },
            toolbar: {
                show: false
            },
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    // this will print mango, apple etc. when clicked on respective datapoint
                    setShowDevices(true);
                    setSelectedDate(deviceVsTimeData.dates[config.dataPointIndex]);
                },
                dataPointMouseEnter: (event) => {
                    event.target.style.cursor = 'pointer';
                }
            }
        },
        xaxis: {
            categories: deviceVsTimeData.dates,
            title: {
                text: 'Date'
            }
        },
        yaxis: {
            title: {
                text: 'Number of Devices'
            }
        },
        tooltip: {
            intersect: true,
            shared: false
        },
        markers: {
            size: 5
        },
        stroke: {
            curve: 'smooth'
        }
    };

    return (
        <MainCard title="Activity Reports Management" cardHeaderTitle="Devices vs Time" cardHeaderShow={true}>
            <Grid container spacing={gridSpacing}>
                <Grid item xs={10}>
                    <Typography sx={{ fontSize: '16px' }}>
                        Provides a line graph showing the count of devices connecting up to the server on a daily basis
                    </Typography>
                </Grid>
                {deviceVsTimeData.values.length > 0 && (
                    <Grid item xs={2}>
                        <ReportFileExports
                            elementId="deviceTime"
                            reportTitle="Devices vs Time"
                            downloadFileName="device_vs_time_report"
                            downloadFileOption="all"
                            data={[deviceVsTimeData.values]}
                            headers={deviceVsTimeData.dates}
                            filtersData={filtersData}
                        />
                    </Grid>
                )}
                <Grid item xs={3}>
                    <SubCard title="Filters">
                        <Grid container>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        maxDate={endDate}
                                        onChange={(newValue) => {
                                            setStartDate(newValue);
                                        }}
                                        renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        maxDate={today}
                                        onChange={(newValue) => {
                                            setEndDate(newValue);
                                        }}
                                        renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Date Range</InputLabel>
                                    <Select
                                        label="Date Range"
                                        value={dateRangeValue}
                                        name="date_range"
                                        onChange={handleChangeDateRangeValue}
                                    >
                                        {dateRangeOptions.map((data, key) => (
                                            <MenuItem value={data.value} key={key}>
                                                {data.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Group</InputLabel>
                                    <Select
                                        label="Select Group"
                                        value={groupId}
                                        name="group"
                                        onChange={(e) => setGroupId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {groupsData.length > 0 &&
                                            groupsData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    sx={{ width: '100%' }}
                                    loading={loading}
                                    onClick={getDeviceTimeData}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={9}>
                    {loading === true ? (
                        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>Please wait...</Typography>
                    ) : deviceVsTimeData.values.length === 0 ? (
                        <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                            No data for the device vs time report
                        </Typography>
                    ) : (
                        <Chart id="deviceTime" options={options} type="line" series={series} width="80%" />
                    )}
                    {showDevices && (
                        <DeviceTimeDevicesDialog
                            title="device_vs_time"
                            groupId={groupId}
                            startDate={selectedDate}
                            groupsData={groupsData}
                            onClose={() => setShowDevices(false)}
                        />
                    )}
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default ReportsDeviceTime;
